import React, { useEffect, useState } from 'react';
import { t } from 'i18next';
import { RadixButton, RadixDialog, RadixSelect } from '@/components/radix';
import { useMediaQuery } from '@/hooks/shared';
import {
    GetApiDoctorsPortalMessagesSortBy,
    GetApiDoctorsPortalMessagesSortOrder,
    GetApiDoctorsPortalMessagesPriority,
} from '@/shared/api/models';

export interface MessagesViewState {
    doctorId: string;
    viewMode: 'card' | 'table';
    sortBy?: string;
    sortOrder?: string;
    priority?: string;
}

export interface MessagesFilterModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    value: MessagesViewState;
    onApply: (state: MessagesViewState) => void;
    doctorOptions: Array<{ value: string; label: string }>;
}

const SORT_BY_NONE = '__none__';
const PRIORITY_ALL = '__all__';

const SORT_BY_OPTIONS = [
    { value: SORT_BY_NONE, label: t('Doctors.FilterSortByNone') },
    // { value: GetApiDoctorsPortalMessagesSortBy.validFrom, label: t('Doctors.FilterSortByValidFrom') },
    { value: GetApiDoctorsPortalMessagesSortBy.validUntil, label: t('Doctors.FilterSortByValidUntil') },
    { value: GetApiDoctorsPortalMessagesSortBy.title, label: t('Doctors.FilterSortByTitle') },
    { value: GetApiDoctorsPortalMessagesSortBy.priority, label: t('Doctors.FilterSortByPriority') },
    { value: GetApiDoctorsPortalMessagesSortBy.isPinned, label: t('Doctors.FilterSortByPinned') },
    { value: GetApiDoctorsPortalMessagesSortBy.createdAt, label: t('Doctors.FilterSortByCreatedAt') },
];

const SORT_ORDER_OPTIONS = [
    { value: GetApiDoctorsPortalMessagesSortOrder.asc, label: t('Doctors.FilterSortOrderAsc') },
    { value: GetApiDoctorsPortalMessagesSortOrder.desc, label: t('Doctors.FilterSortOrderDesc') },
];

const PRIORITY_OPTIONS = [
    { value: PRIORITY_ALL, label: t('Doctors.FilterPriorityAll') },
    { value: GetApiDoctorsPortalMessagesPriority.INFO, label: t('Doctors.PriorityLow') },
    { value: GetApiDoctorsPortalMessagesPriority.IMPORTANT, label: t('Doctors.PriorityMedium') },
    { value: GetApiDoctorsPortalMessagesPriority.CRITICAL, label: t('Doctors.PriorityHigh') },
];

export default function MessagesFilterModal({
    open,
    onOpenChange,
    value,
    onApply,
    doctorOptions,
}: MessagesFilterModalProps) {
    const isDesktop = useMediaQuery('(min-width: 960px)');
    const isMd = useMediaQuery('(min-width: 768px)');
    const [draft, setDraft] = useState<MessagesViewState>(value);

    useEffect(() => {
        if (open) {
            setDraft({ ...value });
        }
    }, [open, value]);

    const handleDone = () => {
        onApply(draft);
        onOpenChange(false);
    };

    return (
        <RadixDialog
            open={open}
            onOpenChange={onOpenChange}
            title={t('Doctors.FilterAndSortTitle')}
            position={isDesktop ? 'center' : 'bottom'}
            footer={
                <div className="flex justify-end">
                    <RadixButton variant="primary" size="base" onClick={handleDone}>
                        {t('Doctors.FilterDone')}
                    </RadixButton>
                </div>
            }
        >
            <div className="flex flex-col gap-4">
                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                        {t('Doctors.SelectDoctorForMessages')}
                    </label>
                    <RadixSelect
                        value={draft.doctorId}
                        onValueChange={(v) => setDraft((s) => ({ ...s, doctorId: v }))}
                        options={doctorOptions}
                        placeholder={t('Doctors.SelectDoctorForMessages')}
                        className="w-full"
                    />
                </div>
                {isMd && (
                    <div>
                        <label className="block text-sm font-medium text-text-primary mb-1">
                            {t('Doctors.ViewModeLabel')}
                        </label>
                        <RadixSelect
                            value={draft.viewMode}
                            onValueChange={(v) => setDraft((s) => ({ ...s, viewMode: v as 'card' | 'table' }))}
                            options={[
                                { value: 'card', label: t('Doctors.ViewCard') },
                                { value: 'table', label: t('Doctors.ViewTable') },
                            ]}
                            className="w-full"
                        />
                    </div>
                )}
                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                        {t('Doctors.FilterSortBy')}
                    </label>
                    <RadixSelect
                        value={draft.sortBy ?? SORT_BY_NONE}
                        onValueChange={(v) => setDraft((s) => ({ ...s, sortBy: v === SORT_BY_NONE ? undefined : v }))}
                        options={SORT_BY_OPTIONS}
                        className="w-full"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                        {t('Doctors.FilterSortOrder')}
                    </label>
                    <RadixSelect
                        value={draft.sortOrder ?? GetApiDoctorsPortalMessagesSortOrder.desc}
                        onValueChange={(v) => setDraft((s) => ({ ...s, sortOrder: v || undefined }))}
                        options={SORT_ORDER_OPTIONS}
                        className="w-full"
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                        {t('Doctors.FilterPriority')}
                    </label>
                    <RadixSelect
                        value={draft.priority ?? PRIORITY_ALL}
                        onValueChange={(v) => setDraft((s) => ({ ...s, priority: v === PRIORITY_ALL ? undefined : v }))}
                        options={PRIORITY_OPTIONS}
                        className="w-full"
                    />
                </div>
            </div>
        </RadixDialog>
    );
}
